// src/components/admin/DialogGuiaEditar.js
//
// Dialog para crear o editar un Guía. Organizado en 4 tabs:
//   1. Identidad — datos personales + acceso al sistema
//   2. Zona de operación — dirección + mapa Google
//   3. Modelo económico — % comisión, bonos, duración
//   4. Datos bancarios — cuenta + certificado bancario verificable
//
// Al crear (guia={}), tab 1 y 4 son requeridos; el resto se puede
// completar después editando. Al editar, todas son opcionales.
// El certificado bancario se sube en Datos bancarios y queda
// pendiente de verificación hasta que el superadmin lo revise.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Divider, MenuItem, Alert, Box,
  InputAdornment, IconButton, Tabs, Tab, Stack,
} from '@mui/material';
import VisibilityIcon    from '@mui/icons-material/Visibility';
import TelefonoInput, { componerE164, esE164Valido } from '../TelefonoInput';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import PersonIcon        from '@mui/icons-material/Person';
import PlaceIcon         from '@mui/icons-material/Place';
import PercentIcon       from '@mui/icons-material/Percent';
import OpenInNewIcon     from '@mui/icons-material/OpenInNew';
import BadgeIcon         from '@mui/icons-material/Badge';

import { guias as apiGuias } from '../../api/guiasApi';
import { useApp } from '../../context/AppContext';
import MapaPinDraggable from '../MapaPinDraggable';
import VerificadorDocumento from '../VerificadorDocumento';
import SelectorMunicipio from '../registro/SelectorMunicipio';

const DEFAULTS = {
  nombreCompleto: '', tipoDocumento: 'CC', numeroDocumento: '',
  telefono: '', correo: '', password: '', fotoUrl: '', rut: '',
  cuentaBancaria: '', tipoCuenta: '', banco: '',
  direccionBase: '', latitud: '', longitud: '', radioOperacionKm: 2.0,
  municipioID: '', barriosCubiertos: '',
  porcentajeComision: 20, bonoAfiliacionInicial: 0, diasParaBono: 90, mesesRecurrente: 0,
  contratoUrl: '',
  // URLs verificadas por /api/holaveci/verificar-documento
  urlCedulaVerificada: '', certificadoBancarioUrl: '',
  // Solo lectura desde backend al editar
  certificadoBancarioVerificado: false,
  fechaVerificacionCedula: null, fechaVerificacionCertificado: null,
};

const TABS = [
  { key: 'identidad',    label: 'Identidad',        icon: <PersonIcon fontSize="small" /> },
  { key: 'zona',         label: 'Zona',             icon: <PlaceIcon fontSize="small" /> },
  { key: 'economico',    label: 'Modelo económico', icon: <PercentIcon fontSize="small" /> },
  { key: 'verificacion', label: 'Verificación',     icon: <BadgeIcon fontSize="small" /> },
];

export default function DialogGuiaEditar({ abierto, guia, onCerrar, onGuardado }) {
  const { notificar } = useApp();
  const esNuevo = !guia?.guiaId;
  const [f, setF] = useState(DEFAULTS);
  // Indicativo separado del numero local — al enviar al backend se compone
  // E.164 con componerE164(). El backend valida con [TelefonoE164] y rechaza
  // strings sin '+' o sin codigo pais.
  const [indicativoTel, setIndicativoTel] = useState('57');
  const [zonaMunicipio, setZonaMunicipio] = useState(null); // { municipioNombre, departamentoNombre, lat, lng } — solo para el geocoder, no se manda al backend
  const [tab, setTab] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [errorDensidad, setErrorDensidad] = useState(null);
  // Error general al guardar (correo duplicado, telefono duplicado, etc.)
  // Se muestra dentro del dialog para que el usuario lo vea aunque el toast
  // se cierre antes de que alcance a leerlo.
  const [errorGuardar, setErrorGuardar] = useState(null);
  const [verPass, setVerPass] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    setTab(0);
    setErrorDensidad(null);
    setErrorGuardar(null);
    setVerPass(false);
    if (guia?.guiaId) {
      apiGuias.obtener(guia.guiaId).then(r => {
        const g = r.guia;
        // Si el telefono viene en E.164 (+573138756155), separar en indicativo
        // + numero local para poblar el TelefonoInput. Se asume '57' cuando
        // no se puede parsear (fallback razonable en CO).
        const telE164 = (g.telefono || '').trim();
        let ind = '57';
        let numLocal = telE164;
        if (telE164.startsWith('+')) {
          // Heuristica simple: para Colombia (+57) el numero nacional son 10
          // digitos. Para otros paises, tomar los 10 ultimos digitos como
          // numero local y el resto como indicativo.
          const digitos = telE164.slice(1);
          if (digitos.length >= 10) {
            ind = digitos.slice(0, digitos.length - 10);
            numLocal = digitos.slice(-10);
          }
        }
        setIndicativoTel(ind);
        setF({
          nombreCompleto: g.nombreCompleto || '', tipoDocumento: g.tipoDocumento || 'CC',
          numeroDocumento: g.numeroDocumento || '', telefono: numLocal,
          correo: g.correo || '', password: '',
          fotoUrl: g.fotoUrl || '', rut: g.rut || '',
          cuentaBancaria: g.cuentaBancaria || '', tipoCuenta: g.tipoCuenta || '',
          banco: g.banco || '',
          direccionBase: g.direccionBase || '',
          latitud: g.latitud ?? '', longitud: g.longitud ?? '',
          radioOperacionKm: g.radioOperacionKm ?? 2.0,
          municipioID: g.municipioID ?? '', barriosCubiertos: g.barriosCubiertos || '',
          porcentajeComision: g.porcentajeComision ?? 20,
          bonoAfiliacionInicial: g.bonoAfiliacionInicial ?? 0,
          diasParaBono: g.diasParaBono ?? 90,
          mesesRecurrente: g.mesesRecurrente ?? 0,
          contratoUrl: g.contratoUrl || '',
          urlCedulaVerificada: g.urlCedulaVerificada || '',
          certificadoBancarioUrl: g.certificadoBancarioUrl || '',
          certificadoBancarioVerificado: g.certificadoBancarioVerificado || false,
          fechaVerificacionCedula: g.fechaVerificacionCedula || null,
          fechaVerificacionCertificado: g.fechaVerificacionCertificado || null,
        });
      });
    } else {
      setF(DEFAULTS);
    }
  }, [abierto, guia]);

  const setC = (k, v) => setF(s => ({ ...s, [k]: v }));

  // ── Validación por tab (solo bloquea al crear) ────────────
  const validarIdentidad = () => {
    if (!f.nombreCompleto.trim()) return 'Nombre completo obligatorio.';
    if (!f.numeroDocumento.trim()) return 'Número de documento obligatorio.';
    if (!f.telefono.trim())        return 'Teléfono obligatorio.';
    if (esNuevo && !f.correo.trim())    return 'Correo obligatorio para acceso al sistema.';
    if (esNuevo && (!f.password || f.password.length < 6))
      return 'Contraseña de mínimo 6 caracteres.';
    return null;
  };

  const validarVerificacion = () => {
    if (esNuevo && !f.urlCedulaVerificada) {
      return 'La cédula del Guía debe subirse y verificarse antes de crear el registro.';
    }
    return null;
  };

  const submit = async (forzar = false) => {
    const errId = validarIdentidad();
    if (errId) { notificar(errId, 'error'); setTab(0); return; }
    const errVer = validarVerificacion();
    if (errVer) { notificar(errVer, 'error'); setTab(3); return; }

    setGuardando(true);
    setErrorDensidad(null);
    setErrorGuardar(null);
    try {
      // Validacion previa: si el numero local no forma un E.164 valido con el
      // indicativo elegido, no dejamos avanzar. El backend tambien valida con
      // [TelefonoE164] pero devolveria 400 sin contexto de UI.
      const telefonoE164 = componerE164(indicativoTel, f.telefono);
      if (!esE164Valido(telefonoE164)) {
        notificar('El teléfono no es válido. Revisa país + número.', 'error');
        setGuardando(false);
        return;
      }
      const dto = {
        ...f,
        telefono: telefonoE164,   // sobrescribe el numero local con el E.164 real
        latitud:  f.latitud === '' ? null : Number(f.latitud),
        longitud: f.longitud === '' ? null : Number(f.longitud),
        radioOperacionKm: Number(f.radioOperacionKm),
        municipioID: f.municipioID === '' ? null : Number(f.municipioID),
        porcentajeComision:    Number(f.porcentajeComision),
        bonoAfiliacionInicial: Number(f.bonoAfiliacionInicial),
        diasParaBono:          Number(f.diasParaBono),
        mesesRecurrente:       Number(f.mesesRecurrente),
      };
      // urlCedulaVerificada y certificadoBancarioUrl SÍ van al backend
      // (los pobló el VerificadorDocumento con la URL retornada por
      // /api/holaveci/verificar-documento). Estos otros son de solo lectura:
      delete dto.certificadoBancarioVerificado;
      delete dto.fechaVerificacionCedula;
      delete dto.fechaVerificacionCertificado;

      let guiaId;
      if (esNuevo) {
        const r = await apiGuias.crear(dto, { forzar });
        guiaId = r.guiaId;
        notificar('Guía registrado con acceso al sistema.', 'success');
      } else {
        await apiGuias.actualizar(guia.guiaId, dto);
        guiaId = guia.guiaId;
        notificar('Guía actualizado.', 'success');
      }
      onGuardado(guiaId);
    } catch (e) {
      if (esNuevo && e.status === 409 && e.data?.densidadActual !== undefined) {
        setErrorDensidad(e.data);
        setTab(1);   // llevar al usuario a la tab de zona
      } else {
        const msg = e.mensajeUsuario || e.data?.mensaje || 'No se pudo guardar.';
        setErrorGuardar(msg);
        notificar(msg, 'error');
      }
    } finally { setGuardando(false); }
  };

  // ── Handler común para el VerificadorDocumento ──────────
  // Ambos documentos (cédula y certificado bancario) usan el mismo endpoint
  // /api/holaveci/verificar-documento. Cuando el backend responde valido=true
  // guarda la URL retornada en el state — al hacer Registrar Guía, las URLs
  // viajan en el JSON del POST /guias.

  // Datos para el cotejo del OCR — parte de nombreCompleto en primer nombre + resto
  const [primerNombre, apellidosCotejo] = useMemo(() => {
    const partes = f.nombreCompleto.trim().split(/\s+/);
    if (partes.length <= 1) return [f.nombreCompleto.trim(), ''];
    return [partes[0], partes.slice(1).join(' ')];
  }, [f.nombreCompleto]);

  return (
    <Dialog open={abierto} onClose={onCerrar} maxWidth="md" fullWidth
            PaperProps={{ sx: { minHeight: '70vh' } }}>
      <DialogTitle sx={{ pb: 0 }}>
        {esNuevo ? 'Nuevo Guía' : `Editar: ${guia?.nombreCompleto}`}
      </DialogTitle>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
            variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        {TABS.map((t, i) => (
          <Tab key={t.key} label={t.label} icon={t.icon} iconPosition="start" sx={{ minHeight: 48 }} />
        ))}
      </Tabs>
      <DialogContent>
        {errorDensidad && (
          <Alert severity="warning" sx={{ mb: 2 }} action={
            <Button size="small" color="warning" onClick={() => submit(true)}>
              Forzar registro
            </Button>
          }>
            {errorDensidad.mensaje}
          </Alert>
        )}

        {/* ═══════════════ TAB 1: IDENTIDAD ═══════════════ */}
        {tab === 0 && (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Datos personales + credenciales para que el Guía entre al sistema.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField fullWidth required label="Nombre completo"
                  value={f.nombreCompleto} onChange={e => setC('nombreCompleto', e.target.value)} />
              </Grid>
              <Grid item xs={4} md={2}>
                <TextField select fullWidth label="Tipo doc" value={f.tipoDocumento}
                  onChange={e => setC('tipoDocumento', e.target.value)}>
                  <MenuItem value="CC">CC</MenuItem>
                  <MenuItem value="CE">CE</MenuItem>
                  <MenuItem value="PA">PA</MenuItem>
                  <MenuItem value="NIT">NIT</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={8} md={2}>
                <TextField fullWidth required label="Número" value={f.numeroDocumento}
                  onChange={e => setC('numeroDocumento', e.target.value)} disabled={!esNuevo} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TelefonoInput
                  indicativo={indicativoTel}
                  numero={f.telefono}
                  onChange={(ind, num) => {
                    setIndicativoTel(ind);
                    setC('telefono', num);
                  }}
                  label="Teléfono"
                  helperText="10 dígitos, sin prefijo. Se envía como +57..."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth required={esNuevo} label="Correo" type="email" value={f.correo}
                  onChange={e => setC('correo', e.target.value)}
                  helperText={esNuevo ? "Usa el correo para entrar al sistema" : ""} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth required={esNuevo}
                  label={esNuevo ? "Contraseña" : "Nueva contraseña (opcional)"}
                  type={verPass ? 'text' : 'password'}
                  value={f.password} onChange={e => setC('password', e.target.value)}
                  helperText={esNuevo ? "Mínimo 6 caracteres" : "Solo si vas a resetearla"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setVerPass(v => !v)}>
                          {verPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="URL de foto (opcional)" value={f.fotoUrl}
                  onChange={e => setC('fotoUrl', e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ═══════════════ TAB 2: ZONA ═══════════════ */}
        {tab === 1 && (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Punto base + radio de operación. Ajusta el pin en el mapa para precisión exacta.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SelectorMunicipio
                  value={f.municipioID}
                  onChange={(v) => setC('municipioID', v)}
                  onSeleccion={setZonaMunicipio}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Dirección base" value={f.direccionBase}
                  onChange={e => setC('direccionBase', e.target.value)}
                  helperText="Ej: Calle 45 #23-15, Bucaramanga" />
              </Grid>
              <Grid item xs={12}>
                <MapaPinDraggable
                  lat={f.latitud === '' ? null : Number(f.latitud)}
                  lng={f.longitud === '' ? null : Number(f.longitud)}
                  onChange={({ lat, lng }) => setF(s => ({ ...s, latitud: lat, longitud: lng }))}
                  direccionParaGeocodificar={
                    // Meter el municipio/departamento DENTRO del texto de
                    // búsqueda es más confiable que solo sesgar por
                    // coordenadas: Google puede ignorar un sesgo blando si
                    // encuentra en otra ciudad un formato de dirección que
                    // le resulta más "seguro" (así "Calle 195 # 80-16" en
                    // Floridablanca terminó resolviendo en Bogotá — 195 es
                    // numeración típica bogotana).
                    zonaMunicipio
                      ? `${f.direccionBase}, ${zonaMunicipio.municipioNombre}, ${zonaMunicipio.departamentoNombre}, Colombia`
                      : f.direccionBase
                  }
                  sesgoUbicacion={zonaMunicipio?.lat && zonaMunicipio?.lng
                    ? { lat: zonaMunicipio.lat, lng: zonaMunicipio.lng }
                    : null}
                  height={260}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth label="Radio (km)" type="number" value={f.radioOperacionKm}
                  inputProps={{ step: 0.5, min: 0.5 }}
                  onChange={e => setC('radioOperacionKm', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Barrios cubiertos (opcional)" value={f.barriosCubiertos}
                  onChange={e => setC('barriosCubiertos', e.target.value)}
                  helperText="Solo informativo — se le muestra al tendero al escanear el QR. No filtra ni restringe nada. Déjalo vacío si tu zona se define solo por el radio." />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ═══════════════ TAB 3: MODELO ECONÓMICO ═══════════════ */}
        {tab === 2 && (
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Defaults: 20% de comisión sobre la base gravable, recurrente sin fin,
              sin bonos. Ajusta según el acuerdo mercantil que firmes con este Guía.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <TextField fullWidth label="Comisión (%)" type="number" value={f.porcentajeComision}
                  inputProps={{ step: 0.5, min: 0, max: 100 }}
                  onChange={e => setC('porcentajeComision', e.target.value)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth label="Meses recurrente" type="number" value={f.mesesRecurrente}
                  inputProps={{ min: 0 }}
                  onChange={e => setC('mesesRecurrente', e.target.value)}
                  helperText="0 = sin fin" />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth label="Bono afiliación (COP)" type="number"
                  value={f.bonoAfiliacionInicial}
                  inputProps={{ step: 1000, min: 0 }}
                  onChange={e => setC('bonoAfiliacionInicial', e.target.value)}
                  helperText="0 = sin bono" />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth label="Días para bono" type="number" value={f.diasParaBono}
                  inputProps={{ min: 0 }}
                  onChange={e => setC('diasParaBono', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="URL contrato mercantil (PDF)" value={f.contratoUrl}
                  onChange={e => setC('contratoUrl', e.target.value)}
                  helperText="Enlace externo al contrato firmado (opcional)" />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ═══════════════ TAB 4: VERIFICACIÓN DOCUMENTAL ═══════════════ */}
        {tab === 3 && (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Verificamos documentos con lectura de código QR / PDF417 + OCR
              y los cruzamos contra los datos escritos en la tab <b>Identidad</b>.
              Si los nombres no coinciden, el documento se rechaza.
            </Typography>

            {/* ── Bloque 1: CÉDULA (obligatorio antes de crear) ── */}
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700 }}>
              1. Cédula del Guía
            </Typography>
            <Box sx={{ mb: 3, mt: 1 }}>
              {f.urlCedulaVerificada ? (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 1 }}
                  action={
                    <Button size="small" startIcon={<OpenInNewIcon />}
                            href={f.urlCedulaVerificada} target="_blank" rel="noopener noreferrer">
                      Ver
                    </Button>
                  }>
                  Cédula verificada
                  {f.fechaVerificacionCedula &&
                    ` el ${new Date(f.fechaVerificacionCedula).toLocaleDateString('es-CO')}`}.
                  {!esNuevo && (
                    <Button size="small" sx={{ ml: 1 }}
                            onClick={() => setF(s => ({ ...s, urlCedulaVerificada: '' }))}>
                      Volver a subir
                    </Button>
                  )}
                </Alert>
              ) : (
                <>
                  {(!f.numeroDocumento || !f.nombreCompleto) && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      Completa <b>nombre completo</b> y <b>número de documento</b> en la
                      tab Identidad antes de subir la cédula — el sistema los cruza
                      contra el OCR del documento.
                    </Alert>
                  )}
                  <VerificadorDocumento
                    titulo=""
                    descripcion="Cédula del Guía (anverso y reverso en el mismo archivo). PDF, JPG o PNG."
                    tipoDocumento="cedula"
                    cedula={f.numeroDocumento}
                    nombres={primerNombre}
                    apellidos={apellidosCotejo}
                    onValido={(_file, res) => {
                      setF(s => ({ ...s, urlCedulaVerificada: res.urlDocumento || '' }));
                    }}
                    onInvalido={() => setF(s => ({ ...s, urlCedulaVerificada: '' }))}
                  />
                </>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* ── Bloque 2: DATOS BANCARIOS ── */}
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700 }}>
              2. Datos bancarios
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Banco" value={f.banco}
                  onChange={e => setC('banco', e.target.value)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField select fullWidth label="Tipo cuenta" value={f.tipoCuenta}
                  onChange={e => setC('tipoCuenta', e.target.value)}>
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="AHORROS">Ahorros</MenuItem>
                  <MenuItem value="CORRIENTE">Corriente</MenuItem>
                  <MenuItem value="NEQUI">Nequi</MenuItem>
                  <MenuItem value="DAVIPLATA">Daviplata</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} md={5}>
                <TextField fullWidth label="Número de cuenta" value={f.cuentaBancaria}
                  onChange={e => setC('cuentaBancaria', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="RUT" value={f.rut}
                  onChange={e => setC('rut', e.target.value)}
                  helperText="Necesario si NO quieres retención en la fuente" />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* ── Bloque 3: CERTIFICADO BANCARIO ── */}
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700 }}>
              3. Certificado bancario
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Carta del banco certificando que la cuenta está a nombre del Guía.
              Se verifica con OCR y se rechaza si el nombre no coincide.
            </Typography>
            <Box sx={{ mb: 2 }}>
              {f.certificadoBancarioUrl ? (
                <Alert severity={f.certificadoBancarioVerificado ? 'success' : 'info'}
                  icon={<CheckCircleIcon />} sx={{ mb: 1 }}
                  action={
                    <Stack direction="row" spacing={1}>
                      <Button size="small" startIcon={<OpenInNewIcon />}
                              href={f.certificadoBancarioUrl} target="_blank" rel="noopener noreferrer">
                        Ver
                      </Button>
                      <Button size="small"
                              onClick={() => setF(s => ({ ...s, certificadoBancarioUrl: '',
                                                                certificadoBancarioVerificado: false }))}>
                        Volver a subir
                      </Button>
                    </Stack>
                  }>
                  Certificado {f.certificadoBancarioVerificado ? 'verificado' : 'subido'}
                  {f.fechaVerificacionCertificado &&
                    ` el ${new Date(f.fechaVerificacionCertificado).toLocaleDateString('es-CO')}`}.
                </Alert>
              ) : (
                <VerificadorDocumento
                  titulo=""
                  descripcion="Certificado bancario a nombre del Guía. PDF, JPG o PNG."
                  tipoDocumento="bancario"
                  cedula={f.numeroDocumento}
                  nombres={primerNombre}
                  apellidos={apellidosCotejo}
                  onValido={(_file, res) => {
                    setF(s => ({ ...s, certificadoBancarioUrl: res.urlDocumento || '' }));
                  }}
                  onInvalido={() => setF(s => ({ ...s, certificadoBancarioUrl: '' }))}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      {errorGuardar && (
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setErrorGuardar(null)}
          sx={{ mx: 3, mt: 0, mb: 0, borderRadius: 1, fontSize: 14 }}
        >
          {errorGuardar}
        </Alert>
      )}

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ flexGrow: 1 }}>
          {tab > 0 && (
            <Button onClick={() => setTab(t => t - 1)}>Atrás</Button>
          )}
        </Box>
        <Button onClick={onCerrar}>Cancelar</Button>
        {tab < TABS.length - 1 ? (
          <Button variant="contained" onClick={() => setTab(t => t + 1)}>
            Siguiente
          </Button>
        ) : (
          <Button variant="contained" onClick={() => submit(false)} disabled={guardando}>
            {guardando ? 'Guardando…' : (esNuevo ? 'Registrar Guía' : 'Guardar cambios')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
